import { useEffect, useRef, useState } from "react";
import type { LiveVishingConfig, VoiceAttackerSpec } from "../scenario/types";
import { useSim, outcomeFromScoringEvent } from "../state/store";
import { audio } from "../lib/audio";
import { useVoiceCall } from "../voice/useVoiceCall";

type Phase = "incoming" | "permission" | "connecting" | "talking" | "error";
type Mode = "voice" | "typed" | null;

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/** Line-art headset glyph for the caller avatar — replaces the default
 *  emoji (2026-07-26: "not this white [emoji] font"), matching the stroke
 *  icon style already used elsewhere in the course (e.g. Terminal.tsx). */
function HeadsetIcon() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" strokeLinecap="round" />
      <rect x="3" y="13" width="4.2" height="6.4" rx="1.8" />
      <rect x="16.8" y="13" width="4.2" height="6.4" rx="1.8" />
      <path d="M19 19.4v.6a2.6 2.6 0 0 1-2.6 2.6H13.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Filled phone-handset glyph for the Answer action. */
function PhoneAnswerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor" aria-hidden>
      <path d="M6.6 2.6c.7-.2 1.4.1 1.8.7l1.7 2.6c.4.6.3 1.4-.2 1.9l-1.4 1.4c-.3.3-.4.8-.2 1.2a13 13 0 0 0 5.3 5.3c.4.2.9.1 1.2-.2l1.4-1.4c.5-.5 1.3-.6 1.9-.2l2.6 1.7c.6.4.9 1.1.7 1.8l-.6 2.1c-.2.8-1 1.4-1.8 1.3-8-.7-14.4-7.1-15.1-15.1-.1-.8.5-1.6 1.3-1.8l2.4-.3Z" />
    </svg>
  );
}

/** Hang-up action reuses the same handset glyph rotated 135° — the standard
 *  mobile-OS convention for "end call" (`.lvc-hangup-ico` applies the
 *  rotation), instead of a separate icon. */

/**
 * Episode 13 — a LIVE AI vishing call embedded in the course. The learner
 * answers a ringing desk phone, is asked to enable the mic (with an
 * illustration of the browser permission prompt) OR type instead, then talks
 * to a real OpenAI Realtime caller steered by a staged playbook. Two explicit
 * action buttons ("Open <tool>" / "Hang up") capture the deterministic
 * outcomes so the compromise never rides on unreliable speech recognition.
 */
export function LiveVishingCall({
  config,
  attacker,
  beatId,
  onEnded,
}: {
  config: LiveVishingConfig;
  attacker: VoiceAttackerSpec;
  beatId: string;
  onEnded: (key?: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>("incoming");
  const [mode, setMode] = useState<Mode>(null);
  const [draft, setDraft] = useState("");
  const transcript = useSim((s) => s.transcript);
  const record = useSim((s) => s.recordOutcome);
  const endedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const typed = mode === "typed";
  const { status, seconds, error, start, hangup, sendText, readyForRemoteAsk } = useVoiceCall(attacker, beatId, typed);

  const toolName = config.remoteToolName ?? "Remote Assist";
  const remoteKey = config.remoteKey ?? "remote";
  const endKey = config.endKey ?? "resisted";

  // Ring on mount; stop on answer/unmount.
  useEffect(() => {
    audio.startRing();
    return () => {
      audio.stopRing();
      hangup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once a mode is chosen, connect the live call.
  useEffect(() => {
    if (mode && status === "idle") {
      setPhase("connecting");
      void start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Reflect the call status into the local phase.
  useEffect(() => {
    if (status === "in_call") setPhase("talking");
    else if (status === "error") setPhase("error");
  }, [status]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [transcript]);

  const answer = () => {
    audio.stopRing();
    setPhase("permission");
  };

  const finish = (key: string, opts?: { opened?: boolean }) => {
    if (endedRef.current) return;
    endedRef.current = true;
    audio.stopRing();
    hangup();
    if (opts?.opened) {
      const ev = attacker.scoringEvents.find((s) => s.id === "opened_remote_tool");
      if (ev) record(outcomeFromScoringEvent(beatId, ev, `opened ${toolName} for the caller`));
    } else {
      const ev = attacker.scoringEvents.find((s) => s.id === "ended_call_safely");
      if (ev) record(outcomeFromScoringEvent(beatId, ev, "ended the call without opening remote access"));
    }
    window.setTimeout(() => onEnded(key), 350);
  };

  const submitDraft = () => {
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    sendText(t);
  };

  return (
    <div className="lvc-layer">
      <div className="lvc-card">
        <div className="lvc-top">
          <div className="lvc-avatar" aria-hidden>
            <HeadsetIcon />
          </div>
          <div className="lvc-caller">
            <div className="lvc-name">{config.callerName}</div>
            <div className="lvc-sub">{config.callerSubtitle ?? ""}</div>
          </div>
          {phase === "talking" && <div className="lvc-timer">{fmt(seconds)}</div>}
        </div>

        {/* Redesigned incoming screen (2026-07-26): illustrated pulsing-ring
            background behind the avatar instead of a plain dark panel, and a
            single modern pill-shaped Answer action instead of the old bare
            circular emoji buttons — Decline is REMOVED, this is a training
            call the learner is always meant to pick up. */}
        {phase === "incoming" && (
          <div className="lvc-body lvc-incoming">
            <div className="lvc-ring-stage" aria-hidden>
              <span className="lvc-ring-pulse lvc-ring-pulse--1" />
              <span className="lvc-ring-pulse lvc-ring-pulse--2" />
              <span className="lvc-ring-pulse lvc-ring-pulse--3" />
              <span className="lvc-ring-avatar">
                <HeadsetIcon />
              </span>
            </div>
            <div className="lvc-ring-label">Incoming call…</div>
            <button type="button" className="lvc-answer-btn" onClick={answer}>
              <span className="lvc-answer-ico">
                <PhoneAnswerIcon />
              </span>
              Answer
            </button>
          </div>
        )}

        {phase === "permission" && (
          <div className="lvc-body lvc-permission">
            <p className="lvc-perm-lead">
              To speak with the caller, your browser needs permission to use your microphone.
            </p>

            {/* Illustration of the browser mic permission prompt. */}
            <div className="lvc-mic-mock" aria-hidden>
              <div className="lvc-mic-mock-bar">
                <span className="lvc-mic-mock-dot" />
                <span className="lvc-mic-mock-url">This site wants to</span>
              </div>
              <div className="lvc-mic-mock-row">
                <span className="lvc-mic-mock-ico">{"\u{1F3A4}"}</span>
                <span>Use your microphone</span>
              </div>
              <div className="lvc-mic-mock-btns">
                <span className="lvc-mic-mock-allow">Allow</span>
                <span className="lvc-mic-mock-block">Block</span>
              </div>
              <div className="lvc-mic-mock-hint">Click <b>Allow</b> when it appears</div>
            </div>

            <div className="lvc-perm-choice">
              <button className="lvc-choice lvc-choice-primary" onClick={() => setMode("voice")}>
                <span className="lvc-choice-ico">{"\u{1F3A4}"}</span>
                <span className="lvc-choice-txt">
                  <b>Allow microphone &amp; talk</b>
                  <small>Speak with the caller like a real phone call</small>
                </span>
              </button>
              <button className="lvc-choice" onClick={() => setMode("typed")}>
                <span className="lvc-choice-ico">{"\u2328\uFE0F"}</span>
                <span className="lvc-choice-txt">
                  <b>Type my answers instead</b>
                  <small>No microphone needed — you'll still hear the caller</small>
                </span>
              </button>
            </div>
          </div>
        )}

        {phase === "connecting" && (
          <div className="lvc-body lvc-connecting">
            <span className="lvc-spinner" /> Connecting…
          </div>
        )}

        {phase === "error" && (
          <div className="lvc-body lvc-error">
            <p>Couldn't start the microphone{error ? ` (${error})` : ""}.</p>
            <button className="lvc-choice lvc-choice-primary" onClick={() => setMode("typed")}>
              <span className="lvc-choice-ico">{"\u2328\uFE0F"}</span>
              <span className="lvc-choice-txt">
                <b>Type my answers instead</b>
                <small>Continue the call by typing</small>
              </span>
            </button>
            <button type="button" className="lvc-hangup-btn lvc-hangup-btn--wide" onClick={() => finish(endKey)}>
              <span className="lvc-hangup-ico">
                <PhoneAnswerIcon />
              </span>
              Hang up
            </button>
          </div>
        )}

        {phase === "talking" && (
          <div className="lvc-body lvc-talking">
            <div className="lvc-transcript" ref={scrollRef}>
              {transcript.length === 0 && <div className="lvc-tr-empty">Listening…</div>}
              {transcript.map((l) => (
                <div key={l.id} className={`lvc-line ${l.role} ${l.final ? "" : "tentative"}`}>
                  <span className="lvc-who">{l.role === "attacker" ? "Caller" : "You"}</span>
                  <span className="lvc-text">{l.text}</span>
                </div>
              ))}
            </div>

            {typed ? (
              <div className="lvc-typebar">
                <input
                  className="lvc-input"
                  value={draft}
                  placeholder="Type your reply…"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitDraft();
                  }}
                  autoFocus
                />
                <button className="lvc-send" onClick={submitDraft} disabled={!draft.trim()}>
                  Send
                </button>
              </div>
            ) : (
              <div className="lvc-mic-live">
                <span className="lvc-mic-dot" /> Microphone live — just talk
              </div>
            )}

            <div className="lvc-callactions">
              <button
                type="button"
                className={`lvc-cta lvc-cta-risk ${readyForRemoteAsk ? "lvc-cta-risk--ready" : "lvc-cta-risk--locked"}`}
                disabled={!readyForRemoteAsk}
                title={readyForRemoteAsk ? undefined : "Unlocks once the caller actually asks you to open it"}
                onClick={() => finish(remoteKey, { opened: true })}
              >
                Open {toolName}
              </button>
              <button className="lvc-cta lvc-cta-hang" onClick={() => finish(endKey)}>
                <span className="lvc-hangup-ico">
                  <PhoneAnswerIcon />
                </span>
                Hang up
              </button>
            </div>
            {!readyForRemoteAsk && (
              <div className="lvc-cta-hint">This unlocks once the caller specifically asks you to open it.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
